import {
  CreditCardIcon,
  BuildingLibraryIcon,
} from "@heroicons/react/24/outline";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BackButton,
  Button,
  ButtonVariant,
  LoadingCircle,
  SingleValueOption,
  Select,
  CardLogo,
  CardTypes,
  useToast,
  Tag,
  TagVariant,
} from "@industriousoffice/react-components";
import {
  ZuoraPaymentMethod,
  ZuoraPaymentMethodTypes,
} from "@/api/opb/opbTypes";
import { opbApi } from "@/api/opb/opbApi";
import AccountSelect from "@/components/AccountSelect";
import Layout from "@/components/Layout";
import { PageTitle } from "@/components/PageTitle";
import { getFullPath } from "@/router/utils/getFullPath";
import { slugs } from "@/router/slugs";
import { usesACH, usesBacs, usesPad, usesSepa } from "@/utils/location";
import { trackEvent, TrackingEvents } from "@/utils/tracking";
import { PaymentConfirmationDialog as ConfirmationDialog } from "./components/PaymentConfirmationDialog";
import { usePaymentMethods } from "./usePaymentMethods";
import { AddPaymentMethodsSection } from "./components/AddPaymentMethodsSection";
import {
  PaymentMethodCard,
  paymentMethodLabel,
} from "./components/PaymentMethodCard";
import { PaymentMethodEditor } from "./components/PaymentMethodEditor";
import { OfflineDirectDebitPanel } from "./components/OfflineDirectDebitPanel";
import { CardFeeNotice } from "./components/CardFeeNotice";
import { SavedPaymentMethodValues } from "./schemas/EditSavedPaymentMethodSchema";
import {
  changeDefaultPaymentMethod,
  isExpiredCard,
} from "./utils/payment-method-settings";
import { supportsOfflineDirectDebit } from "./utils/offline-direct-debit";
import { ZuoraQueryParamsType } from "./types";
import "./payment-methods.css";

interface PaymentConfirmation {
  title: string;
  description: ReactNode;
  label: string;
  action: () => Promise<boolean>;
}

export function PaymentMethods(): ReactElement {
  const { t } = useTranslation("paymentMethods");
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const { component: toastComponent, toast } = useToast();
  const state = usePaymentMethods(accountId);
  const { account, methods, isBusy } = state;
  const location = state.locations.find(
    (value) => value._id === account?.locationId
  );
  const [editing, setEditing] = useState<{
    method: ZuoraPaymentMethod;
    isOpen: boolean;
  }>();
  const [confirmation, setConfirmation] = useState<PaymentConfirmation>();
  const [zuoraData, setZuoraData] = useState<ZuoraQueryParamsType | null>(null);
  const [isPadSuccessOpen, setIsPadSuccessOpen] = useState(false);
  const editedMethod =
    methods.find((value) => value.id === editing?.method.id) ?? editing?.method;
  const defaultMethod = methods.find(
    (value) => value.id === account?.defaultPaymentMethodId
  );
  const cards = methods.filter(
    (value) => value.type === ZuoraPaymentMethodTypes.CreditCard
  );
  const banks = methods.filter(
    (value) => value.type !== ZuoraPaymentMethodTypes.CreditCard
  );
  const bankTypes = [
    {
      type: ZuoraPaymentMethodTypes.ACH,
      enabled: usesACH(location),
      label: "ACH",
    },
    {
      type: ZuoraPaymentMethodTypes.Pad,
      enabled: usesPad(location),
      label: "PAD",
    },
    {
      type: ZuoraPaymentMethodTypes.Bacs,
      enabled: usesBacs(location),
      label: "Bacs",
    },
    {
      type: ZuoraPaymentMethodTypes.Sepa,
      enabled: usesSepa(location),
      label: "SEPA",
    },
  ].filter((value) => value.enabled);

  useEffect(() => {
    setEditing(undefined);
    setConfirmation(undefined);
    setIsPadSuccessOpen(false);
  }, [accountId]);

  useEffect(() => {
    const callbackAccount = searchParams.get("field_passthrough1");
    if (
      searchParams.get("responseFrom") === "Response_From_Submit_Page" &&
      callbackAccount != null
    ) {
      setZuoraData(
        Object.fromEntries(
          searchParams.entries()
        ) as unknown as ZuoraQueryParamsType
      );
      navigate(
        getFullPath(slugs.paymentMethods, { accountId: callbackAccount }),
        { replace: true }
      );
    } else if (accountId == null && state.accounts.length > 0) {
      navigate(
        getFullPath(slugs.paymentMethods, { accountId: state.accounts[0]._id }),
        { replace: true }
      );
    }
  }, [accountId, state.accounts.length, searchParams, navigate]);

  const makeDefault = (methodId: string) => {
    const method = methods.find((value) => value.id === methodId);
    if (account == null || method == null || isBusy) return;
    const action = async () => {
      const success = await state.makeDefault(methodId);
      if (success) toast(t("Your default payment method has been saved."));
      return success;
    };
    const feeApplies =
      method.type === ZuoraPaymentMethodTypes.CreditCard &&
      (location?.creditCardFeeRate ?? 0) > 0;
    if (account.autopay || feeApplies) {
      setConfirmation({
        title: t("Change your default payment method?"),
        label: t("Use as default"),
        description: (
          <>
            <p>{paymentMethodLabel(method)}</p>
            <p>
              {t(
                account.autopay
                  ? "Future automatic payments will use this method."
                  : "This will not turn Auto-Pay on."
              )}
            </p>
            {feeApplies && <CardFeeNotice location={location} />}
          </>
        ),
        action,
      });
    } else action();
  };

  const toggleAutoPay = () => {
    if (account == null || isBusy) return;
    const value = !account.autopay;
    const action = async () => {
      const success = await state.setAutoPay(value);
      if (success) {
        trackEvent(TrackingEvents.AutoPayToggled, { autoPayValue: value });
        toast(t("Your auto-pay preferences have been saved."));
      }
      return success;
    };
    if (!value) {
      setConfirmation({
        title: t("Turn off Auto-Pay?"),
        label: t("Turn Auto-Pay Off"),
        description: (
          <p>
            {t(
              location?.currencyCode === "USD"
                ? "With automatic payments off, a $45 monthly charge will be added to your account at this location, beginning your next billing cycle."
                : "You will need to arrange payment each billing cycle when Auto-Pay is off."
            )}
          </p>
        ),
        action,
      });
    } else {
      setConfirmation({
        title: t("Turn on Auto-Pay?"),
        label: t("Turn Auto-Pay On"),
        description: (
          <>
            <p>
              {t(
                "Turning on Auto-Pay updates your account preference; it does not submit an immediate payment. Check Pay Invoices or ask your location team whether any current invoice still needs payment."
              )}
            </p>
            {defaultMethod?.type === ZuoraPaymentMethodTypes.CreditCard && (
              <CardFeeNotice location={location} />
            )}
          </>
        ),
        action,
      });
    }
  };

  const saveMethod = async (
    values: SavedPaymentMethodValues
  ): Promise<boolean> => {
    if (editedMethod == null) return false;
    const method = editedMethod;
    const success = await state.mutate(async (currentAccount) => {
      switch (method.type) {
        case ZuoraPaymentMethodTypes.CreditCard: {
          const [expMonth, expYear] = values.expirationDate
            .split("/")
            .map(Number);
          await opbApi.updateCreditCard(method.id, {
            expMonth,
            expYear,
            cvv: values.cvv || undefined,
            nickname: values.nickname,
          });
          break;
        }
        case ZuoraPaymentMethodTypes.ACH:
          await opbApi.updateACH(method.id, { nickname: values.nickname });
          break;
        case ZuoraPaymentMethodTypes.Pad:
          await opbApi.updatePad(method.id, {
            nickname: values.nickname,
            accountHolderInfo: values.accountHolderInfo,
          });
          break;
        case ZuoraPaymentMethodTypes.Bacs:
          await opbApi.updateBacs(method.id, {
            nickname: values.nickname,
            accountHolderInfo: values.accountHolderInfo,
          });
          break;
        case ZuoraPaymentMethodTypes.Sepa:
          await opbApi.updateSepa(method.id, {
            nickname: values.nickname,
            accountHolderInfo: values.accountHolderInfo,
          });
          break;
        default:
          throw new Error("Unsupported payment method");
      }
      if (
        values.defaultPayment &&
        currentAccount.defaultPaymentMethodId !== method.id
      ) {
        await changeDefaultPaymentMethod(opbApi, currentAccount._id, method.id);
      }
    });
    if (success) toast(t("Your payment method changes have been saved."));
    return success;
  };

  const renderMethod = (method: ZuoraPaymentMethod) => (
    <PaymentMethodCard
      key={method.id}
      method={method}
      isDefault={method.id === account?.defaultPaymentMethodId}
      isBusy={isBusy}
      onEdit={() => setEditing({ method, isOpen: true })}
      onMakeDefault={() => makeDefault(method.id)}
    />
  );
  const onChangeAccount = (option: SingleValueOption) => {
    if (option?.value != null && !isBusy)
      navigate(getFullPath(slugs.paymentMethods, { accountId: option.value }));
  };

  return (
    <Layout className="payment-methods-page">
      {toastComponent}
      <BackButton onClick={() => navigate(getFullPath("/"))}>
        {t("Back")}
      </BackButton>
      <PageTitle>{t("Payment Methods")}</PageTitle>
      <p className="payment-page-description">
        {t("Manage payment methods and preferences for your account.")}
      </p>
      <div className="payment-account-context">
        <div className="payment-account-label">{t("Account and location")}</div>
        <div>
          <AccountSelect
            locations={state.locations}
            accounts={state.accounts}
            accountId={accountId}
            isLoading={state.isLoading && state.accounts.length === 0}
            isDisabled={isBusy}
            onChange={onChangeAccount}
          />
        </div>
        <p className="payment-account-helper">
          {t("Payment settings apply to the selected account and location.")}
        </p>
      </div>
      {state.isLoading && (
        <div
          className="payment-loading"
          role="status"
          aria-label={t("Loading payment methods")}
        >
          <LoadingCircle spinnerColor="#013e3f" />
        </div>
      )}
      {state.error != null && (
        <div className="payment-error" role="alert">
          <p>{t(state.error.message)}</p>
          <Button
            type="button"
            variant={ButtonVariant.Outline}
            onClick={() => window.location.reload()}
          >
            {t("Try again")}
          </Button>
        </div>
      )}
      {!state.isLoading &&
        state.error == null &&
        state.accounts.length === 0 && (
          <p role="status">
            {t(
              "No billing accounts are available. Please contact your location team."
            )}
          </p>
        )}
      {state.mutationError != null && (
        <p className="payment-error payment-notice" role="alert">
          {t(state.mutationError.message)}
        </p>
      )}
      {!state.isLoading && state.error == null && account != null && (
        <>
          {methods.length === 0 && (
            <aside className="payment-empty-warning" role="status">
              <strong>
                {t(
                  "This account and location don’t have any saved payment methods."
                )}
              </strong>
              <p>
                {t(
                  "Add a payment method, then turn on Auto-Pay to help pay invoices on time."
                )}
              </p>
              {location?.currencyCode === "USD" && (
                <p>
                  {t(
                    "A $45 USD monthly service charge applies when Auto-Pay is off."
                  )}
                </p>
              )}
            </aside>
          )}
          <section
            id="payment-autopay-settings"
            tabIndex={-1}
            className={`account-payment-settings${
              methods.length === 0 ? " account-payment-settings-empty" : ""
            }`}
            aria-labelledby="autopay-heading"
            aria-busy={isBusy}
          >
            <div className="payment-autopay-summary">
              <div className="payment-autopay-heading">
                <h2 id="autopay-heading">
                  {t("Auto-Pay")}{" "}
                  <Tag
                    variant={
                      account.autopay ? TagVariant.Positive : TagVariant.Default
                    }
                    text={t(account.autopay ? "On" : "Off")}
                  />
                </h2>
                {methods.length > 0 && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={account.autopay}
                    aria-label={t("Auto-Pay")}
                    className="payment-autopay-switch"
                    disabled={
                      isBusy ||
                      (!account.autopay &&
                        (defaultMethod == null || isExpiredCard(defaultMethod)))
                    }
                    onClick={toggleAutoPay}
                  >
                    <span />
                  </button>
                )}
              </div>
              <p>
                {t(
                  account.autopay
                    ? "Your default payment method is charged automatically on the invoice due date."
                    : "Adding or changing a payment method will not turn Auto-Pay on."
                )}
              </p>
            </div>
            {methods.length > 0 ? (
              <div className="default-payment-select">
                <label htmlFor="default-payment-method">
                  {t("Default payment method")}
                </label>
                <Select
                  className="payment-single-select"
                  customClasses={{
                    control: "payment-select-control",
                    menu: "payment-select-menu",
                    singleValue: "payment-select-value",
                  }}
                  aria-label={t("Default payment method")}
                  value={
                    defaultMethod == null
                      ? null
                      : {
                          value: defaultMethod.id,
                          label: paymentMethodLabel(defaultMethod),
                        }
                  }
                  options={methods.map((method) => ({
                    value: method.id,
                    label: paymentMethodLabel(method),
                    isDisabled: isExpiredCard(method),
                  }))}
                  isOptionDisabled={(option) => Boolean(option.isDisabled)}
                  isDisabled={isBusy}
                  isSearchable={false}
                  isClearable={false}
                  placeholder={t("Choose a payment method")}
                  onChange={(option) => {
                    if (option?.value != null) makeDefault(option.value);
                  }}
                />
              </div>
            ) : (
              <p>{t("Add a payment method, then turn on Auto-Pay.")}</p>
            )}
            {account.autopay &&
              defaultMethod?.type === ZuoraPaymentMethodTypes.CreditCard &&
              (location?.creditCardFeeRate ?? 0) > 0 && (
                <div className="payment-autopay-fee">
                  <CardFeeNotice location={location} />
                </div>
              )}
          </section>
          {defaultMethod != null && isExpiredCard(defaultMethod) && (
            <p className="payment-notice" role="status">
              {t(
                "Update your card’s expiration date or choose another default before enabling Auto-Pay."
              )}
            </p>
          )}
          {!account.autopay &&
            defaultMethod?.type === ZuoraPaymentMethodTypes.CreditCard && (
              <CardFeeNotice location={location} />
            )}
          <AddPaymentMethodsSection
            key={account._id}
            currentAccount={account}
            currentAccountLocation={location}
            isFirstPaymentMethod={methods.length === 0}
            zuoraData={zuoraData}
            setZuoraData={setZuoraData}
            toast={toast}
            onPadSuccess={() => setIsPadSuccessOpen(true)}
            onPaymentMethodAddSuccess={async () => {
              const shouldShowAutoPay = methods.length <= 1;
              await state.refresh();
              if (shouldShowAutoPay)
                requestAnimationFrame(() => {
                  const settings = document.getElementById(
                    "payment-autopay-settings"
                  );
                  settings?.focus();
                  settings?.scrollIntoView({ block: "nearest" });
                });
            }}
          >
            {({ addCard, addBank, isLoading }) => (
              <div
                className="payment-layout"
                data-testid="payment_methods-grid"
              >
                <section
                  className="payment-panel"
                  aria-labelledby="cards-heading"
                >
                  <div className="payment-panel-heading">
                    <span className="payment-section-icon" aria-hidden="true">
                      <CreditCardIcon width={20} height={20} />
                    </span>
                    <h2 id="cards-heading">{t("Credit and debit cards")}</h2>
                    <span
                      className="payment-card-networks"
                      aria-label={t("Supported card networks")}
                    >
                      {[
                        CardTypes.Visa,
                        CardTypes.Mastercard,
                        CardTypes.Amex,
                        CardTypes.Discover,
                      ].map((type) => (
                        <CardLogo key={type} cardType={type} />
                      ))}
                    </span>
                  </div>
                  <p>{t("Add a card to pay invoices online.")}</p>
                  <div className="payment-method-list">
                    {cards.map(renderMethod)}
                    {cards.length === 0 && (
                      <div className="payment-empty">
                        <strong>{t("No saved cards")}</strong>
                        <p>{t("Add a card to use for this account.")}</p>
                      </div>
                    )}
                  </div>
                  <div className="payment-panel-actions">
                    <Button
                      type="button"
                      variant={ButtonVariant.Outline}
                      isDisabled={isBusy || isLoading || location == null}
                      data-payment-methods-focus
                      className="payment-add-method-button"
                      onClick={addCard}
                    >
                      {t("Add a card")}
                    </Button>
                  </div>
                </section>
                {supportsOfflineDirectDebit(location) && location != null && (
                  <OfflineDirectDebitPanel
                    key={location._id}
                    location={location}
                  />
                )}
                {(bankTypes.length > 0 || banks.length > 0) && (
                  <section
                    className="payment-panel bank-payment-panel"
                    aria-labelledby="banks-heading"
                  >
                    <div className="payment-panel-heading">
                      <span className="payment-section-icon" aria-hidden="true">
                        <BuildingLibraryIcon width={20} height={20} />
                      </span>
                      <h2 id="banks-heading">{t("Bank accounts")}</h2>
                    </div>
                    <p>
                      {t("Manage the bank accounts saved for this account.")}
                    </p>
                    <div className="payment-method-list">
                      {banks.map(renderMethod)}
                      {banks.length === 0 && (
                        <div className="payment-empty">
                          <strong>{t("No saved bank accounts")}</strong>
                          <p>
                            {t(
                              "Add a bank account using one of the available options below."
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="payment-panel-actions">
                      {bankTypes.map((bank) => (
                        <Button
                          key={bank.type}
                          className="payment-add-method-button"
                          type="button"
                          variant={ButtonVariant.Outline}
                          isDisabled={isBusy || isLoading}
                          onClick={() => addBank(bank.type)}
                        >
                          {t("Add {{scheme}} account", { scheme: bank.label })}
                        </Button>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </AddPaymentMethodsSection>
          {location == null && (
            <p className="payment-notice" role="status">
              {t(
                "Location details are unavailable. Please contact your location team before adding a payment method."
              )}
            </p>
          )}
        </>
      )}
      {editedMethod != null && account != null && (
        <PaymentMethodEditor
          key={`${account._id}:${editedMethod.id}`}
          method={editedMethod}
          account={account}
          location={location}
          methods={methods}
          isBusy={isBusy}
          isReadBlocked={state.error != null}
          error={state.error ?? state.mutationError}
          isOpen={editing?.isOpen ?? false}
          onClose={() =>
            setEditing((current) =>
              current == null ? undefined : { ...current, isOpen: false }
            )
          }
          onAfterClose={() => setEditing(undefined)}
          onSave={saveMethod}
          onRemove={() => state.remove(editedMethod.id)}
          onMakeDefault={makeDefault}
          onTurnOffAutoPay={toggleAutoPay}
        />
      )}
      <ConfirmationDialog
        isOpen={confirmation != null}
        isLoading={isBusy}
        title={confirmation?.title}
        description={confirmation?.description}
        confirmLabel={confirmation?.label}
        cancelLabel={t("Go Back")}
        onClose={() => {
          if (!isBusy) setConfirmation(undefined);
        }}
        onCancel={() => {
          if (!isBusy) setConfirmation(undefined);
        }}
        onConfirm={async () => {
          if (confirmation != null && !isBusy) {
            await confirmation.action();
            setConfirmation(undefined);
          }
        }}
      />
      <ConfirmationDialog
        isOpen={isPadSuccessOpen}
        title={t("PAD payment accounts require verification")}
        description={
          <>
            <p>
              {t("Please check your email inbox from an email from")}{" "}
              <strong>Stripe</strong> (support@stripe.com){" "}
              {t("to verify your bank account.")}{" "}
              <strong>{t("Do not delete this email")}</strong>.
            </p>
            <p>
              {t("You will receive two")}{" "}
              <a
                className="payment-link"
                href="https://support.stripe.com/questions/what-are-micro-deposits"
                target="_blank"
                rel="noreferrer"
              >
                micro-deposits
              </a>{" "}
              {t(
                "to your account, which may take up to 3 business days to appear. Check the amounts of these deposits, and follow the steps for verification."
              )}
            </p>
          </>
        }
        cancelLabel={t("OK")}
        onCancel={() => setIsPadSuccessOpen(false)}
        onClose={() => setIsPadSuccessOpen(false)}
      />
    </Layout>
  );
}
